import {getApiBaseUrl, getApiHeaders} from './client'

type StreamEvent = {
  event: string
  data: string
}

export type ApiStreamHandlers<T> = {
  onMessage: (payload: T) => void | Promise<void>
  onError?: (error: Error) => void
  onOpen?: () => void
  retryMs?: number
}

function isTransientConnectionError(error: unknown) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : ''

  return (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed|fetch failed/i.test(message)
  )
}

function parseEventBlock(block: string): StreamEvent | null {
  const lines = block
    .split('\n')
    .map((line) => line.trimEnd())
    .filter(Boolean)

  let event = 'message'
  const dataLines: string[] = []

  for (const line of lines) {
    if (line.startsWith(':')) continue
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim() || event
      continue
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim())
    }
  }

  if (!dataLines.length) {
    return null
  }

  return {
    event,
    data: dataLines.join('\n')
  }
}

async function consumeSseStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: StreamEvent) => void | Promise<void>
) {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const {done, value} = await reader.read()
    if (done) break

    buffer += decoder.decode(value, {stream: true})

    while (buffer.includes('\n\n')) {
      const separatorIndex = buffer.indexOf('\n\n')
      const rawBlock = buffer.slice(0, separatorIndex)
      buffer = buffer.slice(separatorIndex + 2)

      const parsed = parseEventBlock(rawBlock)
      if (parsed) {
        await onEvent(parsed)
      }
    }
  }
}

export function openApiStream<T>(
  path: string,
  handlers: ApiStreamHandlers<T>
): () => void {
  let stopped = false
  let reconnectTimer: number | undefined
  let controller: AbortController | null = null

  const connect = async () => {
    controller = new AbortController()

    try {
      const headers = await getApiHeaders()
      headers.Accept = 'text/event-stream'

      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'GET',
        headers,
        cache: 'no-store',
        signal: controller.signal
      })

      if (!response.ok) {
        throw new Error(
          `Stream request failed: ${response.status} ${response.statusText}`
        )
      }

      if (!response.body) {
        throw new Error('Streaming response body is not available')
      }

      handlers.onOpen?.()

      await consumeSseStream(response.body, async (event) => {
        if (event.event === 'error') {
          let message = 'Unknown stream error'
          try {
            const payload = JSON.parse(event.data) as {message?: string}
            message = payload?.message || message
          } catch {
            message = event.data || message
          }
          throw new Error(message)
        }

        const payload = JSON.parse(event.data) as T
        await handlers.onMessage(payload)
      })

      if (!stopped) {
        reconnectTimer = window.setTimeout(
          () => void connect(),
          handlers.retryMs ?? 1500
        )
      }
    } catch (error) {
      if (stopped || controller?.signal.aborted) {
        return
      }

      if (!isTransientConnectionError(error)) {
        try {
          handlers.onError?.(error as Error)
        } catch (handlerError) {
          console.warn('Stream error handler failed', handlerError)
        }
      }
      reconnectTimer = window.setTimeout(
        () => void connect(),
        handlers.retryMs ?? 1500
      )
    }
  }

  void connect()

  return () => {
    stopped = true
    if (reconnectTimer) window.clearTimeout(reconnectTimer)
    controller?.abort()
  }
}
